import React, { useMemo, useState } from 'react';
import { PageContainer, Tile } from 'components/Layout/PageContainer';
import { Service, useProDataService } from 'services/data.service';
import { useDataRequest } from 'hooks/useDataRequest';
import { useDataServiceQueryResult } from 'hooks/useDataServiceQueryResult';
import { MetricsLoadingSpinner } from 'components/MetricsLoadingSpinner';
import { DataEntry, DataResult } from 'models/data';
import { Pagination } from '@mui/material';
import { useIntl } from 'react-intl';
import { TimeInfo } from 'pages/insights-pro/common/reports/over-time-chart/models';
import { getLabelForDatapoint } from 'pages/insights-pro/graphs/graphUtils';
import { useAppStore } from 'hooks/state/useAppStore';
import { EmptyState } from 'components/EmptyState';

export interface OverTimeCommonData {
  timeInfos: TimeInfo[];
  totalGmvAllItems: number;
  totalCompareGmvAllItems: number;
  maxGmv: number;
  maxShare: number;
  isComparisonActive: boolean;
}

export interface OverTimeItemData {
  timeInfos: TimeInfo[];
}

export interface OverTimeReportProps<T extends OverTimeItemData> {
  service: Service;
  extractItems: (data: DataResult) => T[];
  extractTotals: (data: DataResult | DataEntry) => {
    totalGmv: number;
    compareGmv: number;
  };
  titleId: string;
  testId?: string;
  descriptionId: string;
  additionalTitle: string;
  renderRow: (item: T, commonData: OverTimeCommonData) => React.JSX.Element;
}

export function OverTimeReport<T extends OverTimeItemData>({
  service,
  extractItems,
  extractTotals,
  titleId,
  descriptionId,
  testId = '',
  additionalTitle,
  renderRow,
}: OverTimeReportProps<T>) {
  const { fetchData } = useProDataService(service);
  const intl = useIntl();
  const requestData = useDataRequest();

  const [pageSize] = useState(3);
  const [currentPage, setCurrentPage] = useState(0);

  const extractTimeInfo = (data: DataResult): TimeInfo[] =>
    data.sets.timeSeries.map(entry => {
      const totals = extractTotals(entry);
      return {
        code: getLabelForDatapoint(entry, intl),
        gmv: totals.totalGmv,
        comparisonGmv: totals.compareGmv,
      };
    });

  const { products } = useAppStore();
  const productNumbers = products?.map(p => p);

  const reportState = useDataServiceQueryResult(
    requestData,
    baseData =>
      fetchData('reports/over-time', {
        ...baseData,
        filters: {
          ...baseData.filters,
          productNumbers,
        },
      }),
    response => response,
    ['insights-over-time-report', service]
  );

  const reportData = useMemo(() => {
    if (!reportState.data) {
      return null;
    }
    const isComparisonActive = Boolean(
      requestData.comparisonTimeFilter?.fromDate && requestData.comparisonTimeFilter?.toDate
    );
    const timeInfos = extractTimeInfo(reportState.data);
    const items = extractItems(reportState.data);
    const maxGmv = items
      .flatMap(i => i.timeInfos)
      .flatMap(t => [t.gmv, t.comparisonGmv ?? 0])
      .reduce((a, b) => Math.max(a, b), 0);

    const maxShare = items
      .flatMap(i => i.timeInfos)
      .flatMap(t => [
        t.gmv / (timeInfos.find(ti => ti.code === t.code)?.gmv ?? 0),
        (t.comparisonGmv ?? 0) / (timeInfos.find(ti => ti.code === t.code)?.comparisonGmv ?? 0),
      ])
      .reduce((a, b) => Math.max(a, b), 0);

    const totals = extractTotals(reportState.data);
    return {
      isComparisonActive,
      timeInfos,
      items,
      maxGmv,
      maxShare,
      totalGmvAllItems: totals.totalGmv,
      totalCompareGmvAllItems: totals.compareGmv || 0,
      dataResult: reportState.data,
    };
  }, [reportState.data, intl]);

  const paginatedData = useMemo(() => {
    const startIndex = currentPage * pageSize;
    return (reportData?.items ?? []).slice(startIndex, startIndex + pageSize);
  }, [reportData, currentPage, pageSize]);

  if (!reportData && !reportState.isError) {
    return <MetricsLoadingSpinner />;
  }
  const commonData: OverTimeCommonData | null = reportData
    ? {
        timeInfos: reportData.timeInfos,
        totalGmvAllItems: reportData.totalGmvAllItems,
        totalCompareGmvAllItems: reportData.totalCompareGmvAllItems,
        isComparisonActive: reportData.isComparisonActive,
        maxGmv: reportData.maxGmv,
        maxShare: reportData.maxShare,
      }
    : null;
  const reportItemCount = reportData?.items.length ?? 0;

  return (
    <PageContainer>
      {titleId ? <Tile className="text-lg p-4" titleId={titleId} /> : null}
      <Tile
        titleId={descriptionId}
        additionalTitle={additionalTitle}
        className="pb-2"
        data-testid={testId || undefined}
        isError={reportState.isError}
        errorDetails={reportState.error}
      >
        {commonData &&
          (paginatedData.length === 0 ? (
            <div className="col-span-6 xl:col-span-12 flex justify-center mt-4">
              <EmptyState />
            </div>
          ) : (
            <>
              {paginatedData.map((item, idx) => (
                <div className="xl:col-span-12 col-span-6" key={`report-row-${idx}`}>
                  {renderRow(item, commonData)}
                </div>
              ))}
              {pageSize < reportItemCount && (
                <Pagination
                  className="col-span-6 xl:col-span-12 flex justify-center mt-4"
                  count={Math.ceil(reportItemCount / pageSize)}
                  page={currentPage + 1} // 1-based index for MUI Pagination
                  onChange={(_event, page) => setCurrentPage(page - 1)}
                />
              )}
            </>
          ))}
      </Tile>
    </PageContainer>
  );
}
